import React, { useContext } from 'react';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useLazyQuery } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { useFormContext, useWatch } from 'react-hook-form';
import { CommonDataContext } from 'contexts/CommonDataContext';
import { UploadFile, Preview, Search } from '@mui/icons-material';
import { FinanceProgramFormValues } from 'modules/desking/types';
import { Button, IconButton, Stack, Tooltip } from '@mui/material';
import { useRoutesVisibilityChecks } from 'hooks/useRoutesVisibilityChecks';
import { GetByIdResponse, internalInventoryApi } from 'modules/inventory/api';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

export const EquipmentActions = () => {
  const { setValue, control } = useFormContext<FinanceProgramFormValues>();

  const { isDealerUser } = useContext(CommonDataContext);
  const { checkIfInventoryIsVisible } = useRoutesVisibilityChecks();
  const isInventorySearchVisible = checkIfInventoryIsVisible(isDealerUser);

  const assets = useWatch({ control, name: 'equipment.assets' });
  const selectedEquipment = useWatch({ control, name: 'equipment.selectedEquipment' });

  const { showNotification } = useNotifications();

  const [fetchInventoryById] = useLazyQuery<GetByIdResponse>(internalInventoryApi.getById);

  const { handleOpen: handleOpenUploadEquipmentImagesDialog } = useModal(ModalsKeys.UploadImagesForNewEquipmentDialog);
  const { handleOpen: handleOpenPreviewEquipmentImagesDialog } = useModal(ModalsKeys.PreviewNewEquipmentImagesDialog);
  const { handleOpen: handleOpenPreviewExistingEquipmentImagesDialog } = useModal(
    ModalsKeys.PreviewExistingEquipmentImagesDialog
  );
  const { handleOpen: handleOpenSearchInventoryDialog } = useModal(ModalsKeys.SearchInventoryDialog);

  const onUploadImagesDialogSave = (files: File[]) => {
    setValue('equipment.assets', files);
  };

  const onPreviewImagesConfirm = (files: File[]) => {
    setValue('equipment.assets', files);
  };

  const handleInventorySubmit = async (listOfEquipmentId: string[]) => {
    if (listOfEquipmentId.length !== 1) {
      showNotification('Please select exactly one equipment item', { type: 'warning' });
      return;
    }

    const { data, error } = await fetchInventoryById({ variables: { input: { id: listOfEquipmentId[0] } } });

    if (error || !data?.result?.success) {
      showNotification(data?.result?.error ?? 'Failed to fetch equipment data', { type: 'error' });
      return;
    }

    const { details, price, id } = data.result.entity.equipment;

    const fieldsMap: Partial<Record<keyof FinanceProgramFormValues['equipment'], string | number | null | undefined>> =
      {
        quantity: '1',
        make: details.make,
        model: details.model,
        year: details.year,
        selectedEquipment: id,
        mileage: details.mileage,
        stockId: details.stockId,
        newUsed: details.condition,
        serialNumberVin: details.vin,
        retailPrice: price.retailPrice,
        description: details.description,
        class: details.class ? `Class ${details.class}` : '',
        totalWholesaleCost: price.totalWholesaleCost != null ? String(price.totalWholesaleCost) : undefined,
      };

    (Object.entries(fieldsMap) as [keyof FinanceProgramFormValues['equipment'], string][]).forEach(
      ([field, value]) => value != null && value !== '' && setValue(`equipment.${field}`, value)
    );

    showNotification('Equipment data applied successfully');
  };

  return (
    <Stack direction="row" gap="1rem">
      <RenderWithCondition condition={isInventorySearchVisible}>
        <Button
          size="small"
          variant="contained"
          startIcon={<Search />}
          onClick={event => {
            event.stopPropagation();
            handleOpenSearchInventoryDialog({ onSubmit: handleInventorySubmit });
          }}
        >
          Search Inventory
        </Button>
      </RenderWithCondition>

      <Tooltip title="Upload Equipment Images">
        <IconButton
          color="primary"
          className="icon-btn-bordered"
          onClick={() => {
            handleOpenUploadEquipmentImagesDialog({ onSave: onUploadImagesDialogSave });
          }}
        >
          <UploadFile />
        </IconButton>
      </Tooltip>

      <Tooltip title="Preview Equipment Images">
        <IconButton
          disabled={!assets?.length && !selectedEquipment}
          color="primary"
          className="icon-btn-bordered"
          onClick={() => {
            if (selectedEquipment) {
              handleOpenPreviewExistingEquipmentImagesDialog({ equipmentId: selectedEquipment });
            } else {
              handleOpenPreviewEquipmentImagesDialog({ files: assets ?? [], onConfirm: onPreviewImagesConfirm });
            }
          }}
        >
          <Preview />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};
